// ═══════════════════════════════════════════════════════════════════════════════
// useDisplayName — resolve an OID/UPN into a real display name.
//
// Backed by `state.registeredUsers`, which is loaded once on app startup
// from /api/users/directory.  When the in-memory directory has no match
// (a user who left the org, or never signed in to the Hub), the hook lazily
// resolves that one id server-side (Cosmos → Microsoft Graph) and caches the
// result process-wide.  Returns '' only when even Graph cannot name the user;
// the `<UserName>` component renders its own fallback in that case.
//
// Usage:
//   const name = useDisplayName(authorId);          // "Zach Moore"
//   const name = useDisplayName(entry.actor);       // works with UPN too
//   const lookup = useDisplayNameLookup();          // (id) => name (sync only)
//
// Identity keys (assignee ids, audit log actor ids, lead analyst ids) stay
// UPN/OID-based.  Only the displayed string is rewritten.
// ═══════════════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useReducer } from 'react';
import { useApp } from './useAppState';
import { resolveDisplayName, resolveDisplayNameDetailed } from '../lib/displayName';
import { api } from '../services/api';

// ─── Lazy single-user resolution (Graph fallback) ───────────────────────────
// Process-wide memo so the same id is fetched at most once.  An entry of ''
// means "the server (incl. Graph) confirmed no name" — we cache that too so we
// don't re-hit the API on every render.
const _lazyCache = new Map<string, string>();
const _inFlight = new Set<string>();
const _subscribers = new Set<() => void>();

function lazyGet(idOrUpn: string): string | undefined {
  return _lazyCache.get(idOrUpn.toLowerCase());
}

function lazyResolve(idOrUpn: string): void {
  const key = idOrUpn.toLowerCase();
  if (_lazyCache.has(key) || _inFlight.has(key)) return;
  _inFlight.add(key);
  api.users
    .resolveName(idOrUpn)
    .then(r => { _lazyCache.set(key, r.resolved ? r.displayName : ''); })
    .catch(() => { _lazyCache.set(key, ''); })
    .finally(() => {
      _inFlight.delete(key);
      _subscribers.forEach(fn => fn());
    });
}

/** Resolve a single OID/UPN into a display name. */
export function useDisplayName(
  idOrUpn: string | undefined | null,
  storedDisplayName?: string,
): string {
  const { state } = useApp();
  const [, force] = useReducer((c: number) => c + 1, 0);

  const sync = resolveDisplayNameDetailed(idOrUpn, state.registeredUsers, storedDisplayName);

  // Reach for Graph when the directory has actually loaded and either has no
  // match or only yields a degraded fallback (UPN local part / persisted
  // placeholder like "analyst59").  The directory-loaded guard stops the cold
  // initial render (before /api/users/directory resolves) from firing a
  // request for every name on the page.
  const needsLazy =
    (!sync.name || sync.degraded) &&
    !!idOrUpn &&
    state.registeredUsers.length > 0 &&
    lazyGet(idOrUpn) === undefined;

  useEffect(() => {
    if (!needsLazy || !idOrUpn) return;
    _subscribers.add(force);
    lazyResolve(idOrUpn);
    return () => { _subscribers.delete(force); };
    // `force` is a stable dispatch ref; re-subscribe only when the target changes.
  }, [needsLazy, idOrUpn]);

  if (sync.name && !sync.degraded) return sync.name;
  if (idOrUpn) {
    const lazy = lazyGet(idOrUpn);
    if (lazy) return lazy;
  }
  // Degraded fallback (or '') — shown while/if Graph resolution is pending or
  // came up empty.  Never a raw object id.
  return sync.name;
}

/**
 * Stable resolver function — useful when you need to look up many ids inside
 * a `.map(...)` block without calling the hook in a loop.
 */
export function useDisplayNameLookup(): (
  idOrUpn: string | undefined | null,
  storedDisplayName?: string,
) => string {
  const { state } = useApp();
  return useCallback(
    (idOrUpn, storedDisplayName) =>
      resolveDisplayName(idOrUpn, state.registeredUsers, storedDisplayName),
    [state.registeredUsers],
  );
}
